/* global ClipboardEvent, DataTransfer */

import React, {
  useRef,
  forwardRef,
  useCallback,
  useMemo,
  Fragment
} from 'react'
import PropTypes from 'prop-types'
import {
  classNames,
  useSetState,
  useUpdateRef,
  useGlobalId,
  useEventHandler
} from '@zambezi/sdk/common'

/**
 * Create a React File Input component
 *
 * @param {import('./interfaces').FileInputProps} props
 * @param {import('react').Ref} ref
 * @returns {JSX.Element}
 */
function FileInput (
  {
    disabled,
    className,
    multiple,
    error,
    onFocus,
    onBlur,
    onChange,
    inputProps,
    FileInputRenderer
  },
  ref
) {
  const supportsDelete = useMemo(() => {
    try {
      new ClipboardEvent('').clipboardData || new DataTransfer()
      return true
    } catch (error) {
      return false
    }
  }, [])
  const [
    { files, isDragOver, isFocused, isDragInvalid },
    setState
  ] = useSetState({
    files: [],
    isFocused: false,
    isDragOver: false,
    isDragInvalid: false
  })
  const globalId = useGlobalId('zb-file-input-')
  const { className: inputClassName, id = globalId, tabIndex = 0 } =
    inputProps || {}
  const elementRef = useRef()
  const disabledHandler = disabled ? null : true
  const handleDelete = useCallback(
    (fileToDelete, event) => {
      event.stopPropagation()
      const newFiles = files.filter(file => file !== fileToDelete)
      elementRef.current.files = createFileList(newFiles)
      setState({ files: newFiles })
      onChange(event, newFiles)
    },
    [files]
  )

  useUpdateRef(ref, elementRef, { inputProps, files })
  useEventHandler('paste', handlePaste, document.defaultView)

  return (
    <div
      className={classNames('zb-file-input', className, {
        'zb-file-input-is-hover': isDragOver,
        'zb-file-input-is-invalid': isDragInvalid,
        'zb-file-input-is-focused': isFocused,
        'zb-file-input-is-error': error,
        'zb-file-input-is-disabled': disabled
      })}
      onDragEnter={disabledHandler && handleDragEnter}
      onDragOver={disabledHandler && handleDragEnter}
      onDragLeave={disabledHandler && handleDragLeave}
      onDrop={disabledHandler && handleDrop}
    >
      <FileInputRenderer
        files={files}
        onDelete={supportsDelete ? disabledHandler && handleDelete : null}
        multiple={!!multiple}
        disabled={!!disabled}
        error={!!error}
      >
        <label
          htmlFor={id}
          className={classNames(
            inputClassName,
            'zb-button zb-button-primary zb-file-input-label',
            { 'zb-button-is-disabled': disabled }
          )}
        >
          {multiple ? 'Choose files' : 'Choose a file'}
          <input
            aria-invalid={!!error}
            disabled={!!disabled}
            multiple={!!multiple}
            {...inputProps}
            id={id}
            onBlur={disabledHandler && handleBlur}
            onFocus={disabledHandler && handleFocus}
            tabIndex={disabled ? -1 : tabIndex}
            className='zb-file-input-input'
            type='file'
            onChange={disabledHandler && handleChange}
            ref={elementRef}
          />
        </label>
      </FileInputRenderer>
    </div>
  )

  function handleChange (event) {
    addFiles(event, event.target.files)
  }

  function handleBlur (event) {
    setState({ isFocused: false })
    onBlur(event)
  }

  function handleFocus (event) {
    setState({ isFocused: true })
    onFocus(event)
  }

  function handleDragEnter (event) {
    setState({
      isDragOver: true,
      isDragInvalid: event.dataTransfer.types.indexOf('Files') === -1
    })
    event.preventDefault()
  }

  function handleDragLeave (event) {
    event.preventDefault()
    setState({ isDragOver: false, isDragInvalid: false })
  }

  function addFiles (event, selectedFiles) {
    const updateFiles = supportsDelete && files.length
    if (!supportsDelete || selectedFiles.length) {
      const filesArray = filesToArray(selectedFiles)
      const allFiles =
        updateFiles && multiple
          ? filesArray.reduce(
            (list, file) =>
              list.indexOf(file) === -1 ? list.concat(file) : list,
            files
          )
          : filesArray
      setState({ files: allFiles, isDragOver: false, isDragInvalid: false })
      if (supportsDelete) {
        elementRef.current.files = updateFiles
          ? createFileList(allFiles)
          : selectedFiles
      }
      onChange(event, allFiles)
    } else if (supportsDelete && event.type === 'change') {
      elementRef.current.files = createFileList(files)
    }
  }

  function handleDrop (event) {
    event.preventDefault()
    const { files } = event.dataTransfer
    // Only allow 1 file if not multiple
    if (files.length && (multiple || files.length === 1)) {
      addFiles(event, files)
    } else {
      setState({ isDragOver: false, isDragInvalid: false })
    }
  }

  function handlePaste (event) {
    if (document.activeElement === elementRef.current) {
      event.preventDefault()
      const { files } = event.clipboardData
      // Only allow 1 file if not multiple
      if (files.length && (multiple || files.length === 1)) {
        addFiles(event, files)
      }
    }
  }
}

function createFileList (files) {
  const data = new ClipboardEvent('').clipboardData || new DataTransfer()
  filesToArray(files).forEach(file => data.items.add(file))
  return data.files
}

function filesToArray (files) {
  return Array.prototype.slice.call(files)
}

function noop () {}

const fileInput = forwardRef(FileInput)
fileInput.displayName = 'FileInput'

fileInput.propTypes = {
  error: PropTypes.bool,
  disabled: PropTypes.bool,
  onChange: PropTypes.func,
  onBlur: PropTypes.func,
  onFocus: PropTypes.func,
  multiple: PropTypes.bool,
  FileInputRenderer: PropTypes.elementType,
  inputProps: PropTypes.object
}

fileInput.defaultProps = {
  onChange: noop,
  onBlur: noop,
  onFocus: noop,
  FileInputRenderer
}

function FileInputRenderer ({
  onDelete,
  files,
  multiple,
  disabled,
  error,
  children
}) {
  return (
    <Fragment>
      {files.length ? (
        <dl className={'zb-file-input-files'}>
          {files.map((file, index) => (
            <Fragment key={index}>
              <dt>{file.name}</dt>
              {onDelete && (
                <dd>
                  <button
                    type='button'
                    onClick={onDelete.bind(null, file)}
                    className='zb-button-link'
                    disabled={disabled}
                  >
                    Remove
                  </button>
                </dd>
              )}
            </Fragment>
          ))}
        </dl>
      ) : null}
      {children}
    </Fragment>
  )
}

export default fileInput
